import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Tag,
  Typography,
  Button,
  Input,
  Select,
  Row,
  Col,
  Statistic,
  Badge,
  Spin,
  DatePicker,
} from "antd";
import {
  DollarOutlined,
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined
} from "@ant-design/icons";
import { usePaymentLedger } from "PlatForms/SelfLMS/Hooks/PaymentLedger/usePaymentLedger";
import { PaymentStatusEnum, PaymentStatusLabels, PaymentStatusColors } from "Common/Constants/Enums/PaymentStatusEnum";
import { PaymentSourceTypeEnum, PaymentSourceTypeColors, PaymentSourceTypeLabels } from "Common/Constants/Enums/PaymentSourceTypeEnum";
import dayjs from "dayjs";
import { toast } from "react-toastify";

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const PaymentLedgerListPage = () => {
  const navigate = useNavigate();
  const {
    payments,
    loading,
    filters,
    handlePageChange,
    handleFilterChange,
    refresh
  } = usePaymentLedger();

  console.log(".......................... payment ledger list",payments);

  // Calculate statistics
  const totalRevenue = payments.items.reduce((sum, payment) => sum + payment.amount, 0);
  const totalTransactions = payments.totalCount;
  const successfulPayments = payments.items.filter(p => p.status === PaymentStatusEnum.Paid).length;

  // Format currency
  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Format date - SIMPLE version
  const formatDate = (dateString) => {
    return dayjs(dateString).format("DD/MM/YY HH:mm");
  };


  // Get payment description
  const getPaymentDescription = (payment) => {
    const sourceLabel = PaymentSourceTypeLabels[payment.sourceType];
    return `${sourceLabel}`;
  };

  // Columns for the table
  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
      render: (id) => <Text strong>#{id}</Text>
    },
    {
      title: "Description",
      key: "description",
      render: (_, record) => (
        <div>
          <Text strong style={{ display: 'block' }}>
            {getPaymentDescription(record)}
          </Text>

        </div>
      )
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 100,
      render: (amount, record) => (
        <Text strong >
          {formatCurrency(amount, record.currency)}
        </Text>
      )
    },
    {
      title: "Source",
      dataIndex: "sourceType",
      key: "sourceType",
      width: 120,
      render: (sourceType) => (
        <Tag color={PaymentSourceTypeColors[sourceType]}>
          {PaymentSourceTypeLabels[sourceType]}
        </Tag>
      )
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => (
        <Badge
          status={PaymentStatusColors[status]}
          text={PaymentStatusLabels[status]}
        />
      )
    },
    {
      title: "Date",
      dataIndex: "paidAt",
      key: "paidAt",
      width: 120,
      render: (paidAt) => <Text>{formatDate(paidAt)}</Text>
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          onClick={() => navigate(`/selflearner-dashboard/payment-ledger-detail/${record.id}`)}
        >
          View
        </Button>
      )
    }
  ];

  return (
    <div style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ marginBottom: 8 }}>
          <DollarOutlined style={{ marginRight: 12, color: '#1890ff' }} />
          Payment Transactions
        </Title>
        <Text type="secondary">
          View your payment history
        </Text>
      </div>

      {/* Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="Total Spent"
              value={totalRevenue}
              precision={2}
              prefix="$"
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="Transactions"
              value={totalTransactions}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="Successful"
              value={successfulPayments}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters - Simple */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="Search..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => handleFilterChange({ search: e.target.value })}
              allowClear
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="Status"
              style={{ width: '100%' }}
              value={filters.status}
              onChange={(value) => handleFilterChange({ status: value })}
              allowClear
            >
              {Object.values(PaymentStatusEnum).map(value => (
                <Option key={value} value={value}>
                  {PaymentStatusLabels[value]}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Button
              type="primary"
              onClick={refresh}
              icon={<ReloadOutlined />}
              loading={loading}
              block
            >
              Refresh
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Payments Table - Simple */}
      <Card>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={payments.items}
            rowKey="id"
            pagination={{
              current: payments.pageNumber,
              pageSize: payments.pageSize,
              total: payments.totalCount,
              showSizeChanger: true,
              showTotal: (total) => `${total} items`,
              onChange: handlePageChange
            }}
            size="middle"
          />
        </Spin>
      </Card>
    </div>
  );
};

export default PaymentLedgerListPage;